import * as React from "react";
import {Command as CommandField} from "../components/Command";
import {Field, FieldEvent} from "../components/Field";
import {Input} from "../components/Input";
import {Option, Select} from "../components/Select";
import {StringToHTMLParser} from "../EquipmentSettingsUtils";
import {
    Command,
    DataType,
    GroupData,
    NumberValue,
    RequestInfo,
    SectionItem,
    SectionItemType,
    SelectValue,
    SingleData,
    TextValue,
    ValueAdditionalData,
} from "./models";
import {
    Button,
    Icon,
    icons,
    InputAddon,
    NumberInput,
    SelectInput,
    SelectInputOption,
    SelectInputProps,
    Spinner,
    SpinnerSize,
    TextInput,
    Tooltip
} from "@vebox/etui-core";
import {
    getLocaleDecimalSeparator,
    INPUT_BORDER_BOX,
    SELECT_BORDER_BOX
} from "../../../../components/common/etui_components_styles";
import {Display} from "../../../../components/Display";
import {RestDataLoader} from "../../../../dashboard/widgets/unilever/RestDataLoader";
import {AppTranslations} from "../../../../components/Utils/translations/AppTranslations";
import {VeBoxModal} from "../../../../components/modal/VeBoxModal";
import {isHTMLTagSafe} from "../../../../Utils/Utils";
import {CommandWithValue} from "../components/CommandWithValue";

declare var AppTranslations: AppTranslations;

interface SectionItemBuilderProp {
    itemsDescription: any;
    fieldClassName?: string;
    onDataChanged?: (dataID: any, value: any) => void;
    setErrorMessage?: (dataID: any, errorMessage: string, groupID?: any) => void;
    onFieldChanged?: (fieldID: any, e: FieldEvent) => void;
    onExecuteCommand?: (requestInfo: RequestInfo, name: string, actionGuid?: string, equipmentGuid?: string) => void;
    reload?: () => void;
    reloadSections?: () => void;
}

const equalsToAny = (x, ...X): boolean => X.findIndex((xi) => xi === x) >= 0;

const MapToOptionsList = (map: any): Option[] => {
    const options: Option[] = [];
    // tslint:disable-next-line:forin
    for (const key in map) {
        options.push({
            value: key,
            content: map[key],
        });
    }
    return options;
};

export const ColoredCircle: React.FC<{ color: string }> = (props) => (
    <div
        style={{
            width: 24,
            height: 24,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        }}
    >
        <div
            style={{
                width: 24,
                height: 24,
                backgroundColor: props.color,
                borderRadius: "50%",
            }}
        />
    </div>
)

const ColorAndName = ({color, name}: { color: string, name: string }): JSX.Element => {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
            }}
        >
            {color ? (
                <ColoredCircle color={color}/>
            ) : null}
            <p style={{display: "inline-block"}} className={"capitalize"}>{name}</p>
        </div>
    );
};

const isSingleValid = (itemDataValue: TextValue | NumberValue | SelectValue): boolean => {

    const isNotEmptyValue: boolean = itemDataValue
        ? itemDataValue.value !== undefined && itemDataValue.value !== null && itemDataValue.value !== ""
        : false

    const isValueHTMLSafe = isNotEmptyValue && isHTMLTagSafe(itemDataValue.value);
    const canBeEmpty = itemDataValue.additionalData?.canBeEmpty ?? false;
    return isNotEmptyValue ? isValueHTMLSafe : canBeEmpty;
};

const isGroupValid = (group: GroupData): boolean => {
    return Object.keys(group.values).reduce<boolean>((acc, key) => {
        if (group.values[key]) {
            return acc && isSingleValid(group.values[key].value);
        }
        return acc;
    }, true);
};

type EnhancedSelectionItem = SectionItem & { editing: boolean, editable: boolean, valid?: boolean };

export class SectionItemBuilder extends React.Component<SectionItemBuilderProp, any> {
    public render() {
        return Object.keys(this.props.itemsDescription).map<JSX.Element>((key, index) => {
            const item: EnhancedSelectionItem = this.props.itemsDescription[key];
            if (item.type === SectionItemType.COMMAND || item.type === SectionItemType.COMMAND_WITH_VALUE) {
                return this.commandRenderer(item);
            }
            return this.fieldRenderer(item, index);
        });
    }

    private commandRenderer = (item: SectionItem) => {
        const itemCommand: Command = item.data as Command;
        const execute = () => {
            this.props.onExecuteCommand(item.action, item.name, item.guid, item.equipmentGuid);
        };
        if (item.type === SectionItemType.COMMAND) {
            return (
                <CommandField
                    name={item.name}
                    buttonIconHref={itemCommand.hrefIcon}
                    onClick={execute}
                />
            );
        } else {
            return <CommandWithValue
                name={item.name}
                equipmentGuid={item.equipmentGuid}
                value={item.data as SingleData}
                buttonIconHref={itemCommand.hrefIcon}
                onClick={execute}
                editable={itemCommand.editable}
            />
        }
    }

    private fieldRenderer = (item: EnhancedSelectionItem, index) => {
        const tmp = (name) => (e: FieldEvent) => this.props.onFieldChanged(name, e);
        const itemData: SingleData = item.data as SingleData;
        const itemDataValue = itemData.value;

        const color = getItemColor(itemData);

        const reason = getItemReason(itemData);

        const reasonTextColor = getReasonTextColor(itemData);

        return (
            <>
                <Display isVisible={!(item as SectionItem).updateInfo}>
                    <Field
                        fieldName={
                            item.type === SectionItemType.SINGLE
                                ? color
                                    ? (
                                        <ColorAndName
                                            color={color}
                                            name={item.name}
                                        />
                                    )
                                    : item.name
                                : item.name
                        }
                        showName={item.type !== SectionItemType.SINGLE}
                        editing={item.editing}
                        valid={(item.type === SectionItemType.SINGLE
                            ? isSingleValid(itemDataValue)
                            : isGroupValid(item.data as GroupData)) && item.valid
                        }
                        className={this.props.fieldClassName}
                        key={item.name + index}
                        editable={
                            ((
                                item.type === SectionItemType.SINGLE
                                && equalsToAny(
                                    (item.data as SingleData).type,
                                    DataType.EDITABLE_TEXT,
                                    DataType.EDITABLE_NUMBER,
                                    DataType.SELECT,
                                    DataType.SELECT_WITH_ADDITIONAL_INFORMATION,
                                )
                            ) || item.type === SectionItemType.GROUP)
                            && !((item as SectionItem).updateInfo)
                            && ((item as SectionItem).action !== undefined)
                            && (item.editable !== undefined ? item.editable : true)
                        }
                        onChange={tmp(item.name)}
                    >
                        <div className={"field-value"}>
                            {item.type === SectionItemType.GROUP
                                ? this.groupBuilder(item.name, item.editing, item.data as GroupData)
                                : this.singleBuilder(item.name, item.editing, item.data as SingleData, color)
                            }
                        </div>
                    </Field>
                </Display>
                <UpdatingField item={item} reload={this.props.reload} key={item.name + index}/>
            </>
        );
    }

    private groupBuilder = (groupID: any, editing: boolean, data: GroupData) => {
        return StringToHTMLParser(
            data.layout,
            (str: string, valueKey: string, key: React.Key): JSX.Element => {
                return this.singleBuilderForGroupBuilder(valueKey, editing, data.values[valueKey], key, groupID);
            },
            "",
            0,
            1,
            groupID,
        );
    }

    private singleBuilderForGroupBuilder = (
        dataID: any,
        editing: boolean,
        data: SingleData,
        key?: React.Key,
        groupID?: string,
    ): JSX.Element => {
        const onDataChanged = (ID: any) => (e: any) => {
            const value = e.target.value;
            this.props.onDataChanged(ID, {
                groupID,
                value,
            });
        };

        const unitOfMeasure: string = data.value.additionalData
            ? (data.value.additionalData as ValueAdditionalData).unitOfMeasure
                ? " " + (data.value.additionalData as ValueAdditionalData).unitOfMeasure
                : ""
            : "";

        switch (data.type) {
            case DataType.TEXT: {
                return (
                    <>
                        <Input
                            key={groupID + key}
                            editing={false}
                            value={data.value.value}
                        />
                        {data.value.value ? <label>{unitOfMeasure}</label> : null}
                    </>
                );
            }
            case DataType.EDITABLE_TEXT: {
                return (
                    <>
                        <Input
                            key={groupID + key}
                            editing={editing}
                            value={data.value.value}
                            type={"text"}
                            onChange={onDataChanged(dataID)}
                        />
                        {data.value.value ? <label>{unitOfMeasure}</label> : null}
                    </>
                );
            }
            case DataType.NUMBER: {
                return (
                    <>
                        <Input
                            key={groupID + key}
                            editing={false}
                            value={data.value.value}
                        />
                        {data.value.value ? <label>{unitOfMeasure}</label> : null}
                    </>
                );
            }
            case DataType.EDITABLE_NUMBER: {
                return (
                    <>
                        <Input
                            key={groupID + key}
                            editing={editing}
                            type={"number"}
                            value={(data.value as NumberValue).value}
                            onChange={onDataChanged(dataID)}
                        />
                        {data.value.value ? <label>{unitOfMeasure}</label> : null}
                    </>
                );
            }
            case DataType.SELECT_WITH_ADDITIONAL_INFORMATION:
            case DataType.SELECT: {
                return (
                    <>
                        <Select
                            key={groupID + key}
                            editing={editing}
                            value={(data.value as SelectValue).value}
                            options={MapToOptionsList((data.value as SelectValue).options)}
                            onChange={onDataChanged(dataID)}
                        />
                        {data.value.value ? <label>{unitOfMeasure}</label> : null}
                    </>
                );
            }
            default:
                return null;
        }
    }

    private singleBuilder = (
        dataID: any,
        editing: boolean,
        data: SingleData,
        color: string,
    ): JSX.Element => {
        const onDataChanged = (ID: any) => (e: any) => {
            const value = e.target.value;
            this.props.onDataChanged(ID, {
                value,
            });
        };

        const onNumberChanged = (ID: any) => (e: any) => {
            this.props.onDataChanged(ID, {
                value: e,
            });
        };

        const onSelectChange = (ID: any): SelectInputProps['onChange'] => (value) => {
            this.props.onDataChanged(ID, {
                value: value["value"],
            });
        }

        const unitOfMeasure: string = data.value.additionalData
            ? (data.value.additionalData as ValueAdditionalData).unitOfMeasure
                ? " " + (data.value.additionalData as ValueAdditionalData).unitOfMeasure
                : ""
            : "";
        const label: string = dataID.concat(unitOfMeasure !== "" ? " (" + unitOfMeasure.trim() + ")" : "");
        const reason = getItemReason(data);
        const reasonColor = getReasonTextColor(data);

        switch (data.type) {
            case DataType.TEXT: {
                return (
                    <>
                        <TextInput
                            readOnly={true}
                            value={data.value.value ?? ""}
                            label={label}
                            styles={INPUT_BORDER_BOX as any}
                            startInputAddon={color ? (
                                <InputAddon position={"start"}>
                                    <ColoredCircle color={color}/>
                                </InputAddon>
                            ) : undefined}
                        />
                        {/* data.value.value ? <label>{unitOfMeasure}</label> : null*/}
                    </>
                );
            }
            case DataType.EDITABLE_TEXT: {
                return (
                    <>
                        <TextInput
                            label={label}
                            readOnly={!editing}
                            value={data.value.value ?? ""}
                            onChange={onDataChanged(dataID)}
                            styles={INPUT_BORDER_BOX as any}
                            startInputAddon={color ? (
                                <InputAddon position={"start"}>
                                    <ColoredCircle color={color}/>
                                </InputAddon>
                            ) : undefined}
                            error={this.props.itemsDescription[dataID]?.errorMessage
                                ? {errorMessage: this.props.itemsDescription[dataID]?.errorMessage}
                                : undefined}
                        />
                    </>
                );
            }
            case DataType.NUMBER: {
                return (
                    <>
                        <NumberInput
                            label={label}
                            readOnly={true}
                            value={data.value.value}
                            onChange={onNumberChanged(dataID)}
                            styles={INPUT_BORDER_BOX as any}
                            decimalSeparator={getLocaleDecimalSeparator()}
                            startInputAddon={color ? (
                                <InputAddon position={"start"}>
                                    <ColoredCircle color={color}/>
                                </InputAddon>
                            ) : undefined}
                        />
                    </>
                );
            }
            case DataType.EDITABLE_NUMBER: {
                return (
                    <>
                        <NumberInput
                            label={label}
                            readOnly={!editing}
                            value={(data.value as NumberValue).value}
                            onChange={onNumberChanged(dataID)}
                            styles={INPUT_BORDER_BOX as any}
                            decimalSeparator={getLocaleDecimalSeparator()}
                            startInputAddon={color ? (
                                <InputAddon position={"start"}>
                                    <ColoredCircle color={color}/>
                                </InputAddon>
                            ) : undefined}
                        />
                    </>
                );
            }
            case DataType.SELECT_WITH_ADDITIONAL_INFORMATION:
            case DataType.SELECT: {

                const options = MapToOptionsList((data.value as SelectValue).options).map<SelectInputOption>((x) => ({
                    value: x.value as string,
                    label: x.content as string,
                    renderLabel: () => <p className={"select-input-value"}>{x.content as string}</p>
                }));

                const value = options.find((o) => o.value === (data.value as SelectValue).value);

                const reasonValue = reason ? value.label + " " + reason : undefined;

                return (
                    <SelectInput
                        name={dataID}
                        readOnly={!editing}
                        value={value}
                        label={label}
                        styles={SELECT_BORDER_BOX}
                        options={options}
                        startInputAddon={
                            color ?
                                <InputAddon position={"start"}>
                                    <ColoredCircle color={color}/>
                                </InputAddon>
                                : undefined
                        }
                        endInputAddon={
                            !editing ?
                                <InputAddon position={"end"} style={{position: "initial", top: 0}}>
                                    <AdditionalInformation
                                        reason={reasonValue}
                                        reasonTextColor={reasonColor}
                                    />
                                </InputAddon> : undefined
                        }
                        onChange={onSelectChange(dataID)}
                        error={this.props.itemsDescription[dataID]?.errorMessage
                            ? {errorMessage: this.props.itemsDescription[dataID]?.errorMessage}
                            : undefined}
                    />
                );
            }
            default:
                return null;
        }
    }
}

interface AdditionalInformationProps {
    reason?: string;
    reasonTextColor?: string;
}

const AdditionalInformation: React.FC<AdditionalInformationProps> = (props) => {

    const TooltipMessage = () => (
        <div>
            <p>{props.reason}</p>
        </div>
    );

    return (
        <Display isVisible={props.reason}>
            <div
                className={"flx-row flx-align-center"}
                style={{height: "100%"}}
            >
                <Tooltip message={TooltipMessage}>
                    <Icon
                        glyph={icons.INFO_CIRCLE}
                        size={32}
                        styles={{
                            root: {
                                color: props.reasonTextColor,
                            }
                        }}
                    />
                </Tooltip>
            </div>
        </Display>
    );
};

function getItemColor({value}: SingleData): string {
    return value?.additionalData?.color ?? value?.additionalData?.[value.value]?.color;
}

function getItemReason({value}: SingleData): string {
    return value?.additionalData?.[value?.value]?.reason ?? value?.additionalData?.reason;
}

function getReasonTextColor({value}: SingleData): string {
    return value?.additionalData?.[value.value]?.reasonTextColor ?? value?.additionalData?.reasonTextColor;
}

interface UpdatingFieldProps {
    item: SectionItem,
    reload?: () => void;
}


type UpdatingFieldAbortState = "idle" | "aborting" | "other-error" | "unprocessable-error";

const UpdatingField: React.FC<UpdatingFieldProps> = ({item, reload}) => {

    const [state, setState] = React.useState<UpdatingFieldAbortState>("idle");

    function cancel() {
        setState("aborting");
        RestDataLoader.putJSON(
            `equipment/${item?.updateInfo?.equipmentGuid}/blockEquipment/abort`,
            {},
            () => {
                if (reload) {
                    reload();
                }
            },
            (e) => {
                if (e.errorType === "HTTP_UNPROCESSABLE_ENTITY") {
                    setState("unprocessable-error");
                } else {
                    setState("other-error");
                }
            }
        );
    }

    function closeModal() {
        reload();
        setState("idle");
    }

    const showModal = state === "other-error" || state === "unprocessable-error";

    return (
        <>
            <Display isVisible={showModal}>
                <VeBoxModal.Backdrop style={{position: "fixed", top: 0, left: 0, zIndex: 1010}}>
                    <VeBoxModal>
                        <VeBoxModal.Header title={AppTranslations.errorMessages.errorTitle}/>
                        <VeBoxModal.Section>
                            {state === "other-error"
                                ? AppTranslations.errorMessages.somethingWentWrong
                                : AppTranslations.equipmentSettingsPage.equipmentBlockCannotBeAborted}
                        </VeBoxModal.Section>
                        <VeBoxModal.Actions>
                            <Button variant={"strong"} onClick={closeModal}>
                                {AppTranslations.genericWords.ok}
                            </Button>
                        </VeBoxModal.Actions>
                    </VeBoxModal>
                </VeBoxModal.Backdrop>
            </Display>
            <Display isVisible={item.updateInfo}>
                {() =>
                    <div className={"updating-field"}>
                        <div className={"flx-row"}>
                            <div className={"updating-field-icon centered"}>
                                <Spinner size={SpinnerSize.xSmall}/>
                            </div>
                            <div className={"flx-col flx-center"}>
                                <div className={"updating-field-name"}>
                                    {item.name}
                                </div>
                                <div className={"updating-field-info"}>
                                    {StringToHTMLParser(
                                        item.updateInfo.updatingDescriptionLayout,
                                        (str, valueKey) => {
                                            return (<p className={"inline"}>{item.updateInfo.values[valueKey]}</p>);
                                        },
                                        "inline",
                                        0,
                                        1,
                                        "updateInfo",
                                    )}
                                </div>
                            </div>
                        </div>
                        <Display isVisible={item?.updateInfo?.cancelable}>
                            {() => <Button variant={"strong"} disabled={state !== "idle"} onClick={cancel}>
                                <span className={"capitalize"}>{AppTranslations.genericWords.cancel}</span>
                            </Button>}
                        </Display>
                    </div>
                }
            </Display>
        </>
    );
}